import {
  Box3,
  BufferGeometry,
  Camera,
  Color,
  ColorRepresentation,
  DynamicDrawUsage,
  InstancedBufferAttribute,
  InstancedMesh,
  Intersection,
  Matrix4,
  Mesh,
  Object3D,
  Raycaster,
  ShaderMaterial,
  Sphere,
  Vector3,
} from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';

import { isMesh } from '../../utils/index.js';

import Instance, { isInstance } from './Instance.js';

const _tm = new Matrix4();

const _instanceLocalMatrix = new Matrix4();
const _instanceWorldMatrix = new Matrix4();
const _instanceIntersects: Intersection[] = [];
const _material = new ShaderMaterial({
  fragmentShader: `
    void main() {
      gl_FragColor = vec4(0.0);
    }
  `,
  vertexShader: `
    void main() {
      gl_Position = vec4(0.0);
    }
  `,
});

const _box3 = new Box3();
const _color = new Color();
const _identity = new Matrix4();
const _mesh = new Mesh();
const _sphere = new Sphere();

export const DEFAULT_INSTANCES_LIMIT = 1024;

export const isInstances = (object?: any): object is Instances => {
  return (object as any)?.isInstances === true;
};

const calculateWorldMatrix = (object: Object3D, root: Object3D | null = null) => {
  const matrix = new Matrix4().identity();
  let node: Object3D | null = object;
  while (node !== null && node !== root) {
    node.updateMatrix();
    matrix.multiplyMatrices(node.matrix, matrix);
    node = node.parent;
  }

  return matrix;
};

const roundPowerOfTwo = (value: number) => {
  return Math.pow(2, Math.ceil(Math.log(value) / Math.log(2)));
};
/**
 * Instances is a 3D object that can represent multiple instances
 * of an object including all nested meshes and materials
 */
export class Instances extends InstancedMesh<BufferGeometry, ShaderMaterial> {
  instancedMeshes: InstancedMesh[] = [];
  instances: Instance[] = [];
  matrices: Matrix4[] = [];
  boundingBox: Box3 | null = null;
  boundingSphere: Sphere | null = null;
  type: string = 'Instances';
  isInstances: boolean = true;
  count = 0;
  updateRange = -1;
  buffersNeedsUpdate: boolean = false;
  instancesAutoUpdate: boolean = true;
  instancesNeedsUpdate: boolean = false;
  instancesAutoMount: boolean = true;
  meshes: Set<Mesh> = new Set();
  instancesMeshesIgnorePattern = /plane|shadow/i;
  camera: Camera | null = null;
  instanceGeometry = new BufferGeometry();

  //@ts-ignore
  set frustumCulled(value: boolean) {
    this.instancedMeshes?.forEach((instance) => {
      instance.frustumCulled = value;
    });
  }

  constructor(
    public object: Object3D,
    public limit: number = DEFAULT_INSTANCES_LIMIT
  ) {
    super(new BufferGeometry().setFromPoints([]), _material, 0);

    this.initMeshes();
    this.update();

    this.onBeforeRender = (_, __, camera) => {
      this.camera = camera;
      if (this.instancesAutoUpdate) {
        this.updateInstances(true);
      }
    };
  }

  private initMeshes() {
    const geometries: BufferGeometry[] = [];
    this.object.traverse((child) => {
      if (isMesh(child) && (!child.name || !this.instancesMeshesIgnorePattern.test(child.name))) {
        const matrix = calculateWorldMatrix(child, this.object);
        const geometry = child.geometry.clone().applyMatrix4(matrix);
        geometries.push(geometry);
        const newMesh = new Mesh().copy(child);
        newMesh.geometry = geometry;
        newMesh.name = child.name;
        this.meshes.add(newMesh);
      }
    });

    const mergedGeometry = mergeGeometries(geometries, false);
    if (mergedGeometry) {
      // this.instanceGeometry = mergedGeometry;
      this.instanceGeometry.copy(mergedGeometry);
      // Enable acelerated raycast
      if (this.instanceGeometry.computeBoundsTree) {
        this.instanceGeometry.computeBoundsTree();
      }
    } else {
      console.warn('Instances: No geometries found');
    }
  }

  updateInstanceMatrixBuffer(force = false) {
    const limit = this.limit;

    if (!force && limit <= (this.instanceMatrix?.count || 0)) {
      return;
    }

    const { itemSize, array } = this.instanceMatrix || { itemSize: 16, array: [] as any[] };
    const newArray = new Float32Array(limit * itemSize);

    this.instanceMatrix = new InstancedBufferAttribute(newArray, itemSize).setUsage(
      DynamicDrawUsage
    );

    for (let i = 0; i < array.length; i++) {
      newArray[i] = array[i];
    }

    for (let i = array.length - 1; i < limit; i++)
      _identity.toArray(this.instanceMatrix.array, i * itemSize);

    this.buffersNeedsUpdate = true;
  }

  updateInstanceColorBuffer(force = false) {
    const limit = this.limit;

    if (!force && limit <= (this.instanceColor?.count ?? 0)) {
      return;
    }

    const { itemSize, array } = this.instanceColor ?? { itemSize: 3, array: [] as any[] };
    const newArray = new Float32Array(limit * itemSize);
    this.instanceColor = new InstancedBufferAttribute(newArray, itemSize).setUsage(
      DynamicDrawUsage
    );

    for (let i = 0; i < array.length; i++) {
      newArray[i] = array[i];
    }

    for (let i = array.length - 1; i < limit; i++) {
      newArray[i] = 1;
    }

    this.buffersNeedsUpdate = true;
  }

  updateBuffers(force = false) {
    this.updateInstanceMatrixBuffer(force);
    this.updateInstanceColorBuffer(force);
  }

  updateInstancedMeshes() {
    const { limit, count } = this;

    // ensure all layers are enabled for manage at instance level
    this.layers.enableAll();

    const updateRange = this.updateRange !== count ? Math.min(count, limit) : -1;
    if (this.buffersNeedsUpdate) {
      Array.from(this.meshes).forEach((mesh, idx) => {
        const newInstancedMesh = new InstancedMesh(mesh.geometry, mesh.material, limit);
        newInstancedMesh.name = mesh.name;
        if (this.instanceMatrix) {
          newInstancedMesh.instanceMatrix = this.instanceMatrix;
        }
        if (this.instanceColor) {
          newInstancedMesh.instanceColor = this.instanceColor;
        }
        this.remove(this.instancedMeshes[idx]);
        this.instancedMeshes[idx] = newInstancedMesh;
        this.add(newInstancedMesh);
        // ensure all layers are enabled
        newInstancedMesh.layers.enableAll();
      });

      this.buffersNeedsUpdate = false;
    }

    if (updateRange !== -1) {
      this.instancedMeshes.forEach((instancedMesh) => {
        instancedMesh.count = updateRange;
      });
      if (this.instanceMatrix) {
        this.instanceMatrix.clearUpdateRanges();
        this.instanceMatrix.needsUpdate = true;
      }
      if (this.instanceColor) {
        this.instanceColor.clearUpdateRanges();
        this.instanceColor.needsUpdate = true;
      }
      this.updateRange = updateRange;
      this.boundingBox = null;
      this.boundingSphere = null;
    }
  }

  updateInstances(force: boolean = false, offset?: number, count?: number) {
    if (!force && !this.instancesNeedsUpdate) {
      return;
    }

    const visibleInstances = this.instances.filter(
      (instance) => instance.visible && (!this.camera || instance.layers.test(this.camera.layers))
    );

    this.count = visibleInstances.length;

    let items = visibleInstances;
    this.update();

    if (offset || count) {
      items = items.slice(offset ?? 0, offset && offset + (count ?? items.length));
    }

    // // arrange instances by visibility
    // items.sort((a, b) => {
    //   if (visibleInstances.indexOf(a) !== -1 && visibleInstances.indexOf(b) === -1) return -1;
    //   if (visibleInstances.indexOf(a) === -1 && visibleInstances.indexOf(b) !== -1) return 1;
    //   return 0;
    // });

    const itemOffset = offset ?? 0;
    items.forEach((instance, idx) => {
      instance.setInstances(this);
      instance.instanceKey = itemOffset + idx;
      instance.update();
    });

    this.instancesNeedsUpdate = false;
  }

  updateMorphTargets() {}

  update() {
    this.updateBuffers();
    this.updateInstancedMeshes();
  }

  computeBoundingBox(): void {
    if (this.boundingBox === null) {
      this.boundingBox = new Box3();
    }

    if (this.instanceGeometry.boundingBox === null) {
      this.instanceGeometry.computeBoundingBox();
    }

    this.boundingBox.makeEmpty();

    for (let i = 0; i < this.count; i++) {
      this.getMatrixAt(i, _instanceLocalMatrix);

      _box3.copy(this.instanceGeometry.boundingBox!).applyMatrix4(_instanceLocalMatrix);

      this.boundingBox.union(_box3);
    }
  }

  computeBoundingSphere(): void {
    if (this.boundingSphere === null) {
      this.boundingSphere = new Sphere();
    }

    if (this.instanceGeometry.boundingSphere === null) {
      this.instanceGeometry.computeBoundingSphere();
    }

    this.boundingSphere.makeEmpty();

    for (let i = 0; i < this.count; i++) {
      this.getMatrixAt(i, _instanceLocalMatrix);

      _sphere.copy(this.instanceGeometry.boundingSphere!).applyMatrix4(_instanceLocalMatrix);

      this.boundingSphere.union(_sphere);
    }
  }

  instanceVisibleTest(instance: Instance, camera: Camera) {
    return instance.visible && instance.layers.test(camera.layers);
  }

  raycastAt(instanceId: number, raycaster: Raycaster, intersects: Intersection[]) {
    // If the instance wasn't found or exceeds the parents draw range, bail out
    if (instanceId === -1 || instanceId > this.count) return;

    const matrixWorld = this.matrixWorld;

    if (this.instanceGeometry.boundingSphere === null)
      this.instanceGeometry.computeBoundingSphere();

    this.getMatrixAt(instanceId, _instanceLocalMatrix);
    _instanceWorldMatrix.multiplyMatrices(matrixWorld, _instanceLocalMatrix);
    _sphere.copy(this.instanceGeometry.boundingSphere!);
    _sphere.applyMatrix4(_instanceWorldMatrix);

    if (raycaster.ray.intersectsSphere(_sphere) === false) return;

    // calculate the world matrix for each instance
    // the mesh represents this single instance
    _mesh.matrixWorld = _instanceWorldMatrix;

    this.instancedMeshes.forEach((instance) => {
      _mesh.geometry = instance.geometry;
      _mesh.material = instance.material;

      _mesh.raycast(raycaster, _instanceIntersects);

      // process the result of raycast
      for (let i = 0, l = _instanceIntersects.length; i < l; i++) {
        const intersect = _instanceIntersects[i];
        intersect.instanceId = instanceId;
        intersect.object = this;
        intersects.push(intersect);
      }

      _instanceIntersects.length = 0;
    });
  }

  // @ts-ignore
  override raycast(raycaster: Raycaster, intersects: Intersection[]): void {
    const matrixWorld = this.matrixWorld;
    const raycastTimes = this.count;

    // test with bounding sphere first

    if (this.boundingSphere === null) this.computeBoundingSphere();

    _sphere.copy(this.boundingSphere!);
    _sphere.applyMatrix4(matrixWorld);

    if (raycaster.ray.intersectsSphere(_sphere) === false) return;

    // now test each instance
    for (let instanceId = 0; instanceId < raycastTimes; instanceId++) {
      this.raycastAt(instanceId, raycaster, intersects);
    }
  }

  private ensureLimitBounds(index: number) {
    if (index >= this.limit) {
      this.limit = roundPowerOfTwo(Math.max(index, this.limit));
      this.update();
    }
  }

  setMatrixAt(index: number, matrix: Matrix4) {
    this.ensureLimitBounds(index);
    if (this.instanceMatrix) {
      const { array, itemSize } = this.instanceMatrix;
      const offset = index * itemSize;
      const { elements } = matrix;

      // avoid flagging the buffer for a GPU re-upload when nothing changed;
      // compare in float32 (the buffer's precision) or fractional values
      // would always differ from their float64 counterparts
      let changed = false;
      for (let i = 0; i < itemSize; i++) {
        if (array[offset + i] !== Math.fround(elements[i])) {
          changed = true;
          break;
        }
      }

      if (changed) {
        matrix.toArray(array, offset);
        this.instanceMatrix.needsUpdate = true;
      }
    }
  }

  getMatrixAt(index: number, matrix: Matrix4) {
    if (this.instanceMatrix) {
      matrix.fromArray(this.instanceMatrix.array, index * this.instanceMatrix.itemSize);
    }
  }

  getColorAt(index: number, color: Color) {
    if (this.instanceColor) {
      color.fromArray(this.instanceColor.array, index * 3);
    }
  }

  setColorAt(index: number, color: ColorRepresentation) {
    this.ensureLimitBounds(index);
    if (this.instanceColor) {
      const { array } = this.instanceColor;
      const offset = index * 3;
      _color.set(color);

      // avoid flagging the buffer for a GPU re-upload when nothing changed;
      // compare in float32 (the buffer's precision) or fractional values
      // would always differ from their float64 counterparts
      if (
        array[offset] !== Math.fround(_color.r) ||
        array[offset + 1] !== Math.fround(_color.g) ||
        array[offset + 2] !== Math.fround(_color.b)
      ) {
        _color.toArray(array, offset);
        this.instanceColor.needsUpdate = true;
      }
    }
  }

  setPositionAt(index: number, position: Vector3) {
    this.setMatrixAt(index, _tm.makeTranslation(position.x, position.y, position.z));
  }

  add(object: Object3D) {
    if (this.instancesAutoMount && isInstance(object)) {
      this.instances.push(object);
      object.setInstances(this);
      this.instancesNeedsUpdate = true;
    }

    super.add(object);
    return this;
  }

  remove(object: Object3D) {
    if (this.instancesAutoMount && isInstance(object)) {
      const idx = this.instances.indexOf(object);
      if (idx !== -1) {
        this.instances.splice(idx, 1);
        this.instancesNeedsUpdate = true;
      }
    }

    super.remove(object);
    return this;
  }

  copy(source: this, recursive: boolean = true): this {
    super.copy(source, recursive);

    if (source.instanceMatrix) {
      this.instanceMatrix = source.instanceMatrix.clone() as InstancedBufferAttribute;
    }
    if (source.instanceColor) {
      this.instanceColor = source.instanceColor.clone() as InstancedBufferAttribute;
    }
    this.count = source.count;
    this.limit = source.limit;

    if (source.boundingBox !== null) this.boundingBox = source.boundingBox.clone();
    if (source.boundingSphere !== null) this.boundingSphere = source.boundingSphere.clone();

    return this;
  }

  /**
   * Clones the instance
   *
   *
   * @issue https://spazious.atlassian.net/browse/SPZD-643
   */
  clone(recursive?: boolean): this {
    return new Instances(this.object, this.limit).copy(this, recursive) as this;
  }

  dispose() {
    this.instancedMeshes.forEach((instancedMesh) => {
      instancedMesh.dispose();
    });
  }
}
